import { useEffect } from 'react';
import { useMap } from 'react-map-gl';
import type { Polygon } from 'geojson';

interface VisibleExtentEmitterProps {
  onChange: (polygon: Polygon) => void;
}

/**
 * Headless child of `<Map>` that reports the visible viewport as a rectangle
 * `Polygon` on `moveend`, and once on mount. Debouncing (if any) is owned by the
 * parent `QueryMap`. Renders nothing.
 *
 * @param {VisibleExtentEmitterProps} props - The `onChange` sink that receives the viewport polygon.
 * @return {null} Always renders nothing.
 */
export function VisibleExtentEmitter({ onChange }: VisibleExtentEmitterProps) {
  const { current: map } = useMap();

  useEffect(() => {
    if (!map) {
      return;
    }

    const emit = () => {
      const bounds = map.getBounds();
      const west = bounds.getWest();
      const south = bounds.getSouth();
      const east = bounds.getEast();
      const north = bounds.getNorth();
      onChange({
        type: 'Polygon',
        coordinates: [
          [
            [west, south],
            [east, south],
            [east, north],
            [west, north],
            [west, south],
          ],
        ],
      });
    };

    map.on('moveend', emit);
    emit(); // initial fire

    return () => {
      map.off('moveend', emit);
    };
  }, [map, onChange]);

  return null;
}
